/**
 * InsufficientCreditsBanner — a shared, dismissible banner shown when an AI
 * feature call fails with HTTP 402 `{error:"insufficient_ai_credits"}` (see the
 * ai-orchestrator credit guard). It gives the honest French message and a
 * one-click route to the billing/credits page to recharge.
 *
 * Usage:
 *   catch (err) {
 *     if (isInsufficientCreditsError(err)) setCreditsError(err);
 *     else setError(extractError(err, '…'));
 *   }
 *   {isInsufficientCreditsError(creditsError) && (
 *     <InsufficientCreditsBanner error={creditsError} onDismiss={() => …} />
 *   )}
 *
 * Token-themed (light + dark correct) — no hard-coded colours.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router';

import { ApiError } from '@/lib/api/client';
import {
  parseInsufficientCredits,
  type NormalizedInsufficientCredits,
} from '@/lib/api/ai-credits';
import { useT, tr } from '@/i18n';

const DEFAULT_MESSAGE = tr('shell.components.insufficientCreditsBanner.insufficientAiCreditsTopUpYour');
const BILLING_PATH = '/settings/credits';

/**
 * Type guard: is `err` an `ApiError` 402 signalling insufficient AI credits?
 * Recognizes EVERY backend 402 shape via the shared normalizer (orchestrator,
 * content-service, b2b/memory/rag) — not just the top-level
 * `insufficient_ai_credits` marker. Safe to call with anything.
 */
export function isInsufficientCreditsError(err: unknown): err is ApiError {
  return err instanceof ApiError && parseInsufficientCredits(err.status, err.body) != null;
}

function normOf(err: unknown): NormalizedInsufficientCredits {
  if (err instanceof ApiError) return parseInsufficientCredits(err.status, err.body) ?? {};
  return {};
}

export function InsufficientCreditsBanner({
  error,
  onDismiss,
  className = '',
}: {
  /** The caught error. If it is not an insufficient-credits 402, nothing renders. */
  error: unknown;
  /** Optional dismiss handler — renders a close button when provided. */
  onDismiss?: () => void;
  className?: string;
}) {
  const { t } = useT();
  const navigate = useNavigate();
  const [hidden, setHidden] = useState(false);

  if (!isInsufficientCreditsError(error) || hidden) return null;

  const norm = normOf(error);
  const message = norm.detail || DEFAULT_MESSAGE;
  const target = norm.purchaseUrl || BILLING_PATH;
  // A capped member / an unverifiable wallet must NOT be told to buy credits.
  const noTopUp = norm.code === 'user_budget_exceeded' || norm.code === 'engine_unavailable';

  const dismiss = () => {
    setHidden(true);
    onDismiss?.();
  };

  return (
    <div
      role="alert"
      className={`flex flex-col gap-2 rounded-token border border-warning/40 bg-warning/10 px-3 py-2.5 text-sm text-foreground sm:flex-row sm:items-center sm:justify-between ${className}`.trim()}
    >
      <div className="flex items-start gap-2">
        <span aria-hidden="true" className="mt-0.5 text-base leading-none text-warning">
          ⚡
        </span>
        <div className="min-w-0">
          <p className="font-medium text-warning">{message}</p>
          {norm.code === 'user_budget_exceeded' && (
            <p className="mt-0.5 text-xs text-muted">{t('shell.components.insufficientCreditsBanner.yourPersonalMonthlyCapHasBeen')}{typeof norm.cap === 'number' ? t('shell.components.insufficientCreditsBanner.capCr', { cap: norm.cap }) : ''}{' '}{t('shell.components.insufficientCreditsBanner.askAnAdministratorToRaiseIt')}</p>
          )}
          {!noTopUp && typeof norm.available === 'number' && typeof norm.required === 'number' && (
            <p className="mt-0.5 text-xs text-muted">{t('shell.components.insufficientCreditsBanner.requiredRequiredCrAvailableAvailableCr', { required: norm.required, available: norm.available })}</p>
          )}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2 self-end sm:self-auto">
        {!noTopUp && (
          <button
            type="button"
            onClick={() => navigate(target)}
            className="inline-flex items-center justify-center gap-1.5 rounded-token bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground shadow-elevation-1 transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >{t('shell.components.insufficientCreditsBanner.topUpMyCredits')}</button>
        )}
        {onDismiss && (
          <button
            type="button"
            onClick={dismiss}
            aria-label={t('shell.components.insufficientCreditsBanner.close')}
            className="shrink-0 rounded text-muted hover:text-foreground"
          >
            <span aria-hidden="true">✕</span>
          </button>
        )}
      </div>
    </div>
  );
}
